import { queryOptions } from '@tanstack/react-query';

import { graphqlFetch } from './client';
import { queryKeys } from './keys';
import { graphql } from './gql';
import { getArticleQueryDocument } from './documents/article';
import { issueQueryDocument } from './documents/issue';
import { issuesListQueryDocument } from './documents/issues-list';
import { previewQueryDocument } from './documents/preview';
import type {
	GetIssuesOptionsQueryVariables,
	GetArticlesOptionsQueryVariables,
	ArticleSortKeys,
} from './gql/graphql';
import {
	getQueryCacheOptions,
	defaultQueryClient,
} from '@shared/query-client/query-client';
import {
	AUTH_KEY,
	REFRESH_TOKEN_KEY,
	isAccessTokenExpired,
	isRefreshTokenExpired,
	logoutUserJwt,
} from '@shared/auth';
// Not `@features/login`'s re-export (the shape `pages/issue/model/
// issue.hooks.ts` originally imported this from) -- this file is `shared`,
// the bottom FSD layer, and can't import from `features`. Same real
// function either way; `shared/auth/refresh.ts` already reaches it this
// same way.
import { refreshAccessToken } from '@shared/auth/authentication.hooks';
import { USER_ROLE, isNumeric, type Article, type Issue } from '@/constants';
import config from '@/config';
import { Logger } from '@shared/lib/logger';

const logger = new Logger('queries');

/**
 * Per-entity `queryOptions` factories: one place that knows an entity's key,
 * its fetcher, and how its cache policy is derived.
 *
 * Scope note (2026-08-12), **closed 2026-08-19** — `issue` and `article` were
 * deliberately excluded here because their queryFns carry real branching
 * (offline cached-fallback, JWT/Cognito refresh, admin header selection,
 * redirect-on-missing) that this note assumed "belonged to topic 4's auth
 * work." Checked before finally doing this, rather than assumed again:
 * topic 4's own actionable plan (all 7 steps) never once mentions this file,
 * `pages/issue`, or `pages/article` — it's entirely about `shared/auth/`
 * infrastructure (token storage, refresh, route guards, XSS/CSP, tests).
 * This branching was never actually topic 4's to fix; it just sat here
 * unowned while topic 08's loader item cited topic 4 as the blocker. See
 * `issueOptions`/`articleOptions` below -- ported with their exact existing
 * behaviour preserved (including two known, pre-existing quirks: the
 * anonymous-fetch `try/catch` doesn't actually catch promise rejections
 * since the fetch is returned rather than awaited, and authenticated-but-
 * not-admin readers still get anonymous headers on articles). `styles` is
 * listed in the topic-3 plan but has **no query hook in the app at all**
 * today (`GET_STYLES_QUERY` was only ever read by the service-worker
 * round-trip that was removed as dead code), so there is nothing to wrap —
 * don't add a factory with no caller.
 */

export interface CacheArgs {
	/**
	 * Optional, and omitted by every route `loader` on purpose.
	 *
	 * A loader has no trustworthy connectivity answer to give: it runs
	 * outside React so it can't read `AppContext`'s verified `onlineStatus`,
	 * and `navigator.onLine` -- which these call sites used to read -- only
	 * reports whether an interface is up, so a captive portal, a non-routing
	 * VPN, or a PWA relaunching mid-reconnect all claim `true` while nothing
	 * resolves. Awaiting a fresh reachability check per loader would mean an
	 * extra HEAD request on every navigation, to decide something that
	 * barely matters (below).
	 *
	 * Barely matters, because the query persister already tries the
	 * persisted cache *before* the fetcher runs: on a cache hit the network
	 * is never touched whatever this says. All this flag changes is a cache
	 * *miss* -- attempt the request, or fail without trying. Since that
	 * failure now rejects cleanly rather than resolving a placeholder the
	 * persister would store (see `fetchIssue`), attempting and failing costs
	 * nothing but a doomed request that fails fast while offline.
	 *
	 * So loaders omit it and the entity hooks -- which do have the verified
	 * value, from `AppContext.onlineStatus`, itself `useIsOnline()`'s
	 * reachability-checked result -- pass it as before.
	 */
	isOnline?: boolean;
	isAdmin?: boolean;
	ttlMinutes?: number;
}

export type IssuesVariables = GetIssuesOptionsQueryVariables;

/**
 * `cache` is optional -- omit it (as `homeIssuesLoader`/
 * `homeIssuesAndArticlesLoader` do) to prefetch without guessing
 * staleTime/gcTime from connectivity at all, the same choice
 * `morningstarHomeLoader` already made for its own section prefetch. A route
 * loader has no reliably-*confirmed* online/offline signal to give
 * `getQueryCacheOptions` (only `navigator.onLine`, which can read `true` for
 * a moment right after the app boots offline -- see `queryPersister`'s own
 * comment in `query-client.ts` for the real-device bug that caused);
 * `defaultQueryClient`'s own defaults (1h staleTime, 24h gcTime,
 * offlineFirst) are a safe, connectivity-agnostic baseline for a prefetch
 * that a mounted `useQuery` call (this same key, with the real, verified
 * `onlineStatus` from `AppContext`) supersedes moments later anyway.
 */
export function issuesOptions(variables: IssuesVariables, cache?: CacheArgs) {
	return queryOptions({
		queryKey: queryKeys.issues(),
		queryFn: () => graphqlFetch(issuesQueryDocument, variables),
		...(cache ? getQueryCacheOptions(cache) : {}),
	});
}

/**
 * `pages/issues`' own list page (magazine's `/issues` route). A genuinely
 * different GraphQL operation from `issuesOptions` above (`Issues`, no
 * variables, vs. `GetIssuesOptions`, sortKey/limit/reverse/offset) --
 * extracted from `pages/issues/model/issues.hooks.ts`'s bespoke `useQuery`
 * call 2026-08-19 so `/issues`' route can get a loader the same way
 * `issue`/`article`/`home` did. `queryKeys.issuesList()` is a new, distinct
 * key: the hook this replaces used the literal `['issues']`, colliding
 * with `issuesOptions`'s key for a different query -- see that key's own
 * doc comment.
 */
export function issuesListOptions(cache: CacheArgs) {
	return queryOptions({
		queryKey: queryKeys.issuesList(),
		queryFn: () => graphqlFetch(issuesListQueryDocument, undefined),
		...getQueryCacheOptions(cache),
	});
}

// `cache` optional for the same reason as `issuesOptions` above -- see its
// doc comment.
export function articlesOptions(
	variables: ArticlesVariables,
	cache?: CacheArgs,
) {
	const key: Array<string> = ['articles'];
	if (variables.isFeatured) key.push('featured');
	return queryOptions({
		queryKey: key,
		queryFn: () => graphqlFetch(articlesQueryDocument, variables),
		...(cache ? getQueryCacheOptions(cache) : {}),
	});
}

export type ArticlesVariables = GetArticlesOptionsQueryVariables;

export interface ArticleQuery {
	article: Article | null;
}

export interface ArticleFetchArgs extends CacheArgs {
	/**
	 * Called (and never returned from -- always `throw`s or otherwise never
	 * lets execution continue) when the app is offline and there's no cached
	 * copy of this article to fall back to. A component's `useQuery` and a
	 * route's `loader` need different things here: the component wants to
	 * navigate back to wherever the reader came from (`pages/article/ui/
	 * article.tsx`'s existing `back()`); a `loader` should `throw
	 * redirect(...)` instead (TanStack's own idiom -- verified this
	 * propagates correctly through `ensureQueryData`, not assumed), since
	 * `useNavigate()` isn't available outside a component.
	 */
	onUnavailableOffline: () => void;
}

/**
 * Ported from `pages/article/model/article.hooks.ts`'s `getArticle`,
 * behaviour preserved exactly -- see this file's top doc comment for the
 * two known quirks kept as-is rather than fixed here.
 */
export function articleOptions(id: string, args: ArticleFetchArgs) {
	return queryOptions({
		queryKey: queryKeys.article(id),
		queryFn: () => fetchArticle(id, args),
		...getQueryCacheOptions(args),
	});
}

async function fetchArticle(
	id: string,
	args: ArticleFetchArgs,
): Promise<ArticleQuery> {
	const isAdmin = args.isAdmin ?? localStorage.getItem(USER_ROLE) === 'admin';
	const cached = defaultQueryClient.getQueryData<ArticleQuery>(
		queryKeys.article(id),
	);

	// `=== false`, not falsy: `undefined` means "no answer given" (a route
	// loader -- see `CacheArgs.isOnline`), which takes the normal fetch path
	// below so a real failure stays a real failure. Only an explicit `false`,
	// from a caller that actually knows (an entity hook, via
	// `AppContext.onlineStatus`), skips the request.
	if (args.isOnline === false) {
		if (!cached) {
			args.onUnavailableOffline();
			// Throws rather than resolving `{ article: null }`, for the same
			// reason `fetchIssue` below does -- a resolved placeholder is a
			// *successful* fetch as far as the query persister is concerned,
			// so it gets written into IndexedDB under this article's key and
			// shadows the real copy persisted while online, permanently.
			// `onUnavailableOffline` above is documented as never returning
			// (a loader `throw redirect(...)`s; a component navigates), so
			// this is only reached on the component path, where the navigate
			// it triggers is asynchronous and execution does continue here.
			throw new Error('Article unavailable offline: nothing cached');
		}
		return cached;
	}

	const isAuthenticated = localStorage.getItem(AUTH_KEY) === 'true';

	if (!isAuthenticated) {
		try {
			return graphqlFetch(
				getArticleQueryDocument,
				{ id },
				{ withAuth: false },
			) as unknown as Promise<ArticleQuery>;
		} catch (e) {
			logger.error(e);
			if (cached) return cached;
		}
	}

	if (isAdmin) {
		return graphqlFetch(getArticleQueryDocument, {
			id,
		}) as unknown as Promise<ArticleQuery>;
	}

	// Authenticated-but-not-admin still sends anonymous headers -- the
	// pre-existing behaviour this port preserves, not fixes (see this
	// file's top doc comment).
	return graphqlFetch(
		getArticleQueryDocument,
		{ id },
		{
			withAuth: false,
		},
	) as unknown as Promise<ArticleQuery>;
}

export interface IssueQuery {
	issue: Issue | null;
}

export interface IssueFetchParams {
	limit?: number;
	reverse?: boolean;
	sortKey?: ArticleSortKeys;
}

export interface IssueFetchArgs extends CacheArgs, IssueFetchParams {
	/**
	 * Called when a signed-in reader's session can't be refreshed (missing
	 * or expired refresh token, or a failed refresh with nothing cached to
	 * fall back to) and there's nothing left to do but send them home. Same
	 * component-vs-loader split as `ArticleFetchArgs.onUnavailableOffline`
	 * -- a component calls `navigate({ to: '/' })`, a loader `throw
	 * redirect({ to: '/' })`s.
	 */
	onSessionExpired: () => void;
}

/**
 * Ported from `pages/issue/model/issue.hooks.ts`'s `getIssue`, behaviour
 * preserved exactly -- including the two `logoutUserJwt()` branches, which
 * this deliberately leaves calling that function directly (a hard
 * `window.location.href` redirect) rather than routing them through
 * `onSessionExpired` too: they're a *logout*, not a "come back later" bounce
 * -- see `shared/lib/hooks/use-go-back.ts`'s neighbouring investigation this
 * session for why hard-reloading on an actual logout is the deliberate,
 * correct choice, not a bug to convert.
 */
export function issueOptions(id: string | number, args: IssueFetchArgs) {
	return queryOptions({
		queryKey: queryKeys.issue(id, {
			limit: args.limit,
			sortKey: args.sortKey,
			reverse: args.reverse,
		}),
		// `signal` is what makes this query cancellable: TanStack Query only
		// aborts a fetch whose `queryFn` consumed it, and `graphqlFetch`
		// forwards it to the request itself.
		queryFn: ({ signal }) => fetchIssue(id, args, signal),
		...getQueryCacheOptions(args),
	});
}

async function fetchIssue(
	id: string | number,
	args: IssueFetchArgs,
	signal?: AbortSignal,
): Promise<IssueQuery> {
	const isAdmin = args.isAdmin ?? localStorage.getItem(USER_ROLE) === 'admin';
	const queryKey = queryKeys.issue(id, {
		limit: args.limit,
		sortKey: args.sortKey,
		reverse: args.reverse,
	});
	const cached = defaultQueryClient.getQueryData<IssueQuery>(queryKey);

	// `=== false`, not falsy: `undefined` means "no answer given" (a route
	// loader -- see `CacheArgs.isOnline`), which takes the normal fetch path
	// below so a real failure stays a real failure. Only an explicit `false`,
	// from a caller that actually knows (an entity hook, via
	// `AppContext.onlineStatus`), skips the request.
	if (args.isOnline === false) {
		if (cached) {
			return cached;
		}
		// Throws rather than resolving to `{ issue: null }` -- the same fix
		// `fetchPreviewIssue` below already carries, and the exact defect its
		// comment calls out as still living here. Resolving a placeholder made
		// this a *successful* fetch, so the query persister
		// (`shared/query-client/query-client.ts`) wrote `{ issue: null }` into
		// IndexedDB under this issue's own key -- overwriting the real, good
		// copy persisted while online. Every later load then restored that
		// null and failed identically, including "Try again", which re-runs
		// the loader against the same poisoned entry: one transient miss (a
		// cold boot where `restoreQueries` hasn't populated the in-memory
		// cache yet, so `cached` above is undefined) turned into a permanent
		// "not downloaded for offline reading" for an issue the reader had
		// genuinely read online minutes earlier. Reproduced on a real iOS
		// device in airplane mode, 2026-09-02.
		//
		// A rejection is never persisted, so the good entry survives, and
		// `persisterFn` restores it on the next attempt instead. `issueRoute`'s
		// loader (app/routing/route-catalog.tsx) swallows this specific
		// rejection the same way `previewViewRoute`'s already does.
		throw new Error('Issue unavailable offline: nothing cached');
	}

	const isAuthenticated = localStorage.getItem(AUTH_KEY) === 'true';
	const variables = {
		id,
		isSlug: !isNumeric(id),
		limit: args.limit,
		sortKey: args.sortKey,
		reverse: args.reverse,
	};

	// Mirrors the old `let client` that started anonymous
	// (getHTTPHeaders(false)) and was only rebuilt with credentials once the
	// JWT/Cognito block below had confirmed a usable access token.
	let withAuth = false;

	if (!isAuthenticated) {
		try {
			return graphqlFetch(issueQueryDocument, variables, {
				withAuth: false,
				signal,
			}) as unknown as Promise<IssueQuery>;
		} catch (e) {
			logger.error(e);
			if (cached) return cached;
		}
	}

	if (isAdmin) {
		return graphqlFetch(issueQueryDocument, variables, {
			signal,
		}) as unknown as Promise<IssueQuery>;
	}

	if (
		config?.app?.auth?.enabled &&
		(config?.app?.auth?.type === 'jwt' ||
			config?.app?.auth?.type === 'cognito')
	) {
		const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
		if (!refreshToken) {
			logoutUserJwt();
			return { issue: null };
		}

		const refreshExpired = isRefreshTokenExpired();
		if (refreshExpired) {
			logoutUserJwt();
			return { issue: null };
		}

		let accessExpired = isAccessTokenExpired();
		if (accessExpired) {
			for (let i = 0; i < 3; i++) {
				try {
					await refreshAccessToken(refreshToken);
					accessExpired = false;
					break;
				} catch (e) {
					logger.error(e);
					continue;
				}
			}
		}

		if (accessExpired) {
			if (cached) return cached;
			args.onSessionExpired();
			return { issue: null };
		}

		withAuth = true;
	}

	return graphqlFetch(issueQueryDocument, variables, {
		withAuth,
		signal,
	}) as unknown as Promise<IssueQuery>;
}

/**
 * Backs `pages/preview`'s `useGetIssue` and `previewViewRoute`'s own
 * `loader` (`route-catalog.tsx`) with the same factory -- same shape as
 * `issueOptions`/`fetchIssue` above, but the actual GraphQL operation
 * (`previewQueryDocument`) is deliberately different and lighter (no
 * article bodies/styles, just enough for a cover + TOC), and this call
 * site has no JWT/Cognito refresh branching to replicate: `useGetIssue`
 * never had any, so none is added here. The route's `defaultPreload:
 * 'intent'` (`create-tenant-app.tsx`) already preloads this route's lazy
 * chunk on hover; without a `loader`, the *data* fetch only ever started
 * once `Content()` actually mounted -- after the click, not the hover. A
 * `loader` calling `ensureQueryData(previewIssueOptions(...))` makes intent-
 * preload warm this exact query (same `queryKeys.preview(id)` key either
 * hook or loader populates it under), so a genuine hover-then-click reuses
 * the already-in-flight/settled request instead of starting a second one.
 */
export function previewIssueOptions(id: string, args: CacheArgs) {
	return queryOptions({
		queryKey: queryKeys.preview(id),
		queryFn: () => fetchPreviewIssue(id, args),
		...getQueryCacheOptions(args),
	});
}

async function fetchPreviewIssue(
	id: string,
	args: CacheArgs,
): Promise<IssueQuery> {
	const queryKey = queryKeys.preview(id);
	const cached = defaultQueryClient.getQueryData<IssueQuery>(queryKey);

	// `=== false`, not falsy: `undefined` means "no answer given" (a route
	// loader -- see `CacheArgs.isOnline`), which takes the normal fetch path
	// below so a real failure stays a real failure. Only an explicit `false`,
	// from a caller that actually knows (an entity hook, via
	// `AppContext.onlineStatus`), skips the request.
	if (args.isOnline === false) {
		if (cached) {
			return cached;
		}
		// Deliberately *throws* here rather than resolving to `{ issue: null
		// }` (unlike `fetchIssue`'s own offline branch above, which
		// pre-dates this query having a route `loader` at all) -- resolving
		// would get written into the query cache as this key's data, which
		// corrupts `useGetIssue`'s own `isUnavailableOffline` check
		// (`getQueryData(...) === undefined`) for good: once *anything* is
		// cached under this key, that check reads "we have an answer"
		// forever after, even though the answer is a placeholder, not a
		// real fetch. `previewViewRoute`'s `loader` (route-catalog.tsx)
		// catches this specific rejection and swallows it -- a route-level
		// crash isn't the right UI for "offline, haven't seen this issue
		// yet"; `Content()` already renders `OfflineContent` for exactly
		// that once it mounts and re-derives the same signal itself.
		throw new Error('Preview unavailable offline: nothing cached');
	}

	return graphqlFetch(previewQueryDocument, {
		id,
		isSlug: !isNumeric(id),
	}) as unknown as Promise<IssueQuery>;
}

export const issuesQueryDocument = graphql(`
	query GetIssuesOptions(
		$sortKey: IssueSortKeys
		$limit: Int
		$reverse: Boolean
		$offset: Int
	) {
		issues(
			sortKey: $sortKey
			limit: $limit
			reverse: $reverse
			offset: $offset
		) {
			id
			name
			description
			features
			cover
			externalUrl
			price
			currency
			slug
			data
			releaseDate
			updated
			isPaid
			created
			isPurchased
			index
			isFree
			pages
		}
	}
`);

export const articlesQueryDocument = graphql(`
	query GetArticlesOptions($isFeatured: Boolean) {
		articles(isFeatured: $isFeatured) {
			id
			name
			IssueID
			slug
			thumbnail
			featureImage
			index
			isFeatured
			created
			updated
			role
			features
		}
	}
`);
